import { Box, Button, Checkbox, Dialog, DialogContent, DialogTitle, FormControlLabel, Grid, Typography } from '@material-ui/core';
import React from 'react';
import { StyledButton } from '../../../../../App/Shifamed/Components/forms/presenters/StyledListButton';
import Text from '../../../../components/Text';
import { DialogHandler } from '../../../../hooks/useDialog';
import { ApprovalConfigurationUsage, ApprovalConfigurationUsageType, SelectedUsageEntry } from '../../panels/ApprovalConfigurations/types';
import useStyles from './dialog.styles';
import UsageEntry from './UsageEntry';

interface ApprovalConfigurationUsageDialogProps {
  usage: ApprovalConfigurationUsage
  usageDialog: DialogHandler
  onConfirm: (selectedEntries: SelectedUsageEntry[]) => void
}

const ApprovalConfigurationUsageDialog: React.FC<ApprovalConfigurationUsageDialogProps> = ({
  usage,
  usageDialog,
  onConfirm,
}) => {
  const classes = useStyles();

  const allEntries = [
    ...(usage.changeRequestDetails ?? []).map(entry => ({
      ...entry,
      type: 'changeRequest' as const,
    })),
    ...(usage.docRevDetails ?? []).map(entry => ({
      ...entry,
      type: 'documentRevision' as const,
    })),
  ];

  const [selectedEntries, setSelectedEntries] = React.useState<SelectedUsageEntry[]>([]);

  const isAllSelected
    = allEntries.length > 0
    && selectedEntries.length === allEntries.length;

  const handleSelectAll = () => {
    if (isAllSelected) {
      setSelectedEntries([]);
      return;
    }

    setSelectedEntries(allEntries.map(({ id, type }) => ({ id, type })));
  };

  const handleSelectionChange = (
    id: string,
    type: ApprovalConfigurationUsageType,
  ) => {
    setSelectedEntries((currentSelectedEntries) => {
      const isAlreadySelected = currentSelectedEntries.some(
        entry => entry.id === id && entry.type === type,
      );

      if (isAlreadySelected) {
        return currentSelectedEntries.filter(
          entry => !(entry.id === id && entry.type === type),
        );
      }

      return [
        ...currentSelectedEntries,
        { id, type },
      ];
    });
  };

  const isSelected = (
    id: string,
    type: ApprovalConfigurationUsageType,
  ) =>
    selectedEntries.some(
      entry => entry.id === id && entry.type === type,
    );

  return (
    <Dialog
      disableEscapeKeyDown
      disableBackdropClick
      open={usageDialog.isOpen}
      maxWidth="md"
      fullWidth
      classes={{
        paper: classes.usagePaper,
      }}
    >
      <DialogTitle>
        <Text translation="approvers.usage.dialog.title.msg" />
      </DialogTitle>

      <DialogContent className={classes.usageDialogContent}>
        <Typography
          variant="subtitle2"
          color="textSecondary"
          className={classes.usageSubtitle}
        >
          <Text translation="approvers.usage.dialog.subtitle.msg" />
        </Typography>

        <FormControlLabel
          className={classes.selectControl}
          control={
            <Checkbox
              checked={isAllSelected}
              onChange={handleSelectAll}
              color="primary"
            />
          }
          label="Select All"
        />

        <Grid container spacing={3}>
          {!!usage.changeRequestDetails?.length && (
            <Grid item xs={6}>
              <fieldset className={classes.usageSection}>
                <legend className={classes.usageLegend}>
                Change Requests
                </legend>

                {usage.changeRequestDetails.map(entry => (
                  <UsageEntry
                    key={entry.id}
                    entry={entry}
                    checked={isSelected(entry.id, 'changeRequest')}
                    onChange={() =>
                      handleSelectionChange(entry.id, 'changeRequest')
                    }
                  />
                ))}
              </fieldset>
            </Grid>
          )}
          {!!usage.docRevDetails?.length && (
            <Grid item xs={6}>
              <fieldset className={classes.usageSection}>
                <legend className={classes.usageLegend}>
                Documents
                </legend>

                {usage.docRevDetails.map(entry => (
                  <UsageEntry
                    key={entry.id}
                    entry={entry}
                    checked={isSelected(entry.id, 'documentRevision')}
                    onChange={() =>
                      handleSelectionChange(entry.id, 'documentRevision')
                    }
                  />
                ))}
              </fieldset>
            </Grid>
          )}
        </Grid>
      </DialogContent>

      <Box className={classes.actionBtns}>
        <StyledButton
          onClick={usageDialog.close}
          variant="text"
        >
          <Text translation="common.cancel" />
        </StyledButton>
        <Button
          color="primary"
          variant="contained"
          onClick={() => onConfirm(selectedEntries)}
        >
          <Text translation="common.continue" />
        </Button>
      </Box>
    </Dialog>
  );
};

export default ApprovalConfigurationUsageDialog;
